import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  CalendarDays, 
  Users,
  Home,
  Euro,
  ShieldAlert,
  LogOut,
  Menu,
  X,
  Briefcase,
  ChevronRight,
  ClipboardList,
  Tags,
  Camera,
  RefreshCw,
  AlertTriangle,
  Timer,
  FileSignature,
  ShieldCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Avatar component for sidebar user
const UserAvatar = ({ user, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base'
  };
  
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };
  
  const getRoleColor = (role) => {
    switch(role) {
      case 'admin': return 'bg-gradient-to-br from-purple-500 to-purple-700';
      case 'technicien': return 'bg-gradient-to-br from-blue-500 to-blue-700';
      case 'housekeeper': return 'bg-gradient-to-br from-emerald-500 to-emerald-700';
      default: return 'bg-gradient-to-br from-amber-400 to-amber-600';
    }
  };

  if (user?.avatar_url) {
    // Support Cloudinary URLs (full https) and local paths
    const imgSrc = user.avatar_url.startsWith('http') 
      ? user.avatar_url 
      : `${API_URL}/api/${user.avatar_url}`;
    
    return (
      <div className={`${sizeClasses[size]} rounded-xl overflow-hidden ring-2 ring-white/20 shadow-md flex-shrink-0`}>
        <img 
          src={imgSrc} 
          alt={user.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div className={`w-full h-full ${getRoleColor(user.role)} items-center justify-center text-white font-semibold hidden`}>
          {getInitials(user.name)}
        </div>
      </div>
    );
  }
  
  return (
    <div className={`${sizeClasses[size]} rounded-xl ${getRoleColor(user?.role)} flex items-center justify-center text-white font-semibold ring-2 ring-white/20 shadow-md flex-shrink-0`}>
      {getInitials(user?.name)}
    </div>
  );
};

const Sidebar = () => {
  const { user, logout, isAdmin, isTech } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const canDoHousekeeping = isTech && user?.is_also_housekeeper;

  const navItems = [
    { to: isAdmin ? '/admin' : '/tech', icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/admin/planning', icon: CalendarDays, label: 'Planning' },
    ...(isAdmin ? [
      { to: '/admin/users', icon: Users, label: 'Équipes' },
      { to: '/admin/properties', icon: Home, label: 'Logements' },
      { to: '/admin/suivi-logements', icon: ClipboardList, label: 'Suivi logements' },
      { to: '/admin/galerie', icon: Camera, label: 'Galerie photos' },
      { to: '/admin/signalements', icon: AlertTriangle, label: 'Signalements' },
      { to: '/admin/surveillance', icon: ShieldAlert, label: 'Surveillance', highlight: true },
      { to: '/admin/stats-temps', icon: Timer, label: 'Stats temps' },
      { to: '/admin/tarifs', icon: Tags, label: 'Tarifs' },
      { to: '/admin/payments', icon: Euro, label: 'Paiements' },
      { to: '/admin/contrats', icon: FileSignature, label: 'Contrats' },
      { to: '/admin/cautions', icon: ShieldCheck, label: 'Cautions' },
    ] : []),
    ...(canDoHousekeeping ? [
      { to: '/app', icon: Briefcase, label: 'Mes missions', highlight: true },
    ] : []),
  ];

  const NavItem = ({ to, icon: Icon, label, highlight }) => (
    <NavLink
      to={to}
      onClick={() => setIsOpen(false)}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-200 ${
          isActive 
            ? 'bg-white text-blue-900 font-semibold shadow-sm' 
            : highlight
              ? 'bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30 border border-emerald-400/30'
              : 'text-slate-300 hover:bg-white/10 hover:text-white'
        }`
      }
      data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={2} />
      <span className="flex-1">{label}</span>
      {highlight && (
        <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full font-medium">
          Ménage
        </span>
      )}
    </NavLink>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        className="md:hidden fixed top-4 left-4 z-50 p-3 bg-blue-900 text-white rounded-xl shadow-lg hover:bg-blue-800 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="mobile-menu-btn"
      >
        {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-72 bg-blue-900 z-40 transform transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 flex flex-col`}>
        
        {/* Header with Full Logo */}
        <div className="p-5 border-b border-white/10">
          <img 
            src="/logo-mcb-full.png" 
            alt="PlayMCB" 
            className="h-16 w-auto object-contain"
          />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-4 mb-3">
            Navigation
          </p>
          {navItems.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
        </nav>

        {/* User Card */}
        <div className="p-4 border-t border-white/10">
          <div className="bg-white/10 rounded-2xl p-4 mb-3">
            <div className="flex items-center gap-3">
              <UserAvatar user={user} size="md" />
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate text-sm">{user?.name}</p>
                <p className="text-slate-400 text-xs capitalize">{user?.role === 'admin' ? 'Administrateur' : user?.role}</p>
              </div>
            </div>
          </div>
          
          {/* Refresh App Button */}
          <Button
            variant="ghost"
            className="w-full justify-start text-emerald-300 hover:text-emerald-100 hover:bg-emerald-500/20 rounded-xl h-12 mb-2"
            onClick={() => {
              // Clear service worker cache and reload
              if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistrations().then(registrations => {
                  registrations.forEach(registration => {
                    registration.update();
                  });
                });
                caches.keys().then(names => {
                  names.forEach(name => {
                    caches.delete(name);
                  });
                });
              }
              window.location.reload(true);
            }}
            data-testid="refresh-app-btn"
          >
            <RefreshCw className="h-5 w-5 mr-3" />
            Actualiser l'app
          </Button>
          
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/10 rounded-xl h-12"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5 mr-3" />
            Déconnexion
          </Button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
